// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel, { SwiperStyle } from "@eisinc/App/Components/XngCarousel";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { CarouselCardType } from "@eisinc/XDL/types";
import { hideElementOnPrint } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendImpressionTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendImpressionTracking";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductCarouselProduct from "@insite/content-library/Components/ProductCarouselProduct";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { createRef, Fragment, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import { ProductCarouselProductStyles } from "@eisinc/Overrides/Components/ProductCarouselProduct";
import ProductCarouselProductSkeleton from "@eisinc/App/Components/ProductCarouselProductSkeleton/ProductCarouselProductSkeleton";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    widgetPosition = "widgetPosition",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    visibleElementsTitle = "visibleElementsTitle",
    titleAlignment = "titleAlignment",
    customCSS = "customCSS",
    // XNG-Changes
    view = "view",
    showAvailability = "showAvailability",
    uppercaseTitle = "uppercaseTitle",
    italicizeTitle = "italicizeTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.widgetPosition]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.visibleElementsTitle]: string;
        [fields.titleAlignment]: string;
        [fields.customCSS]: string;
        [fields.view]: CarouselCardType;
        // XNG-Changes
        [fields.showAvailability]: boolean;
        [fields.italicizeTitle]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
    pageType: getCurrentPage(state).type,
    productRecommendationWidgets: state.components.productRecommendations.widgets,
    session: state.context.session,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadCarouselProducts,
    sendImpressionTracking,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    // XNG-Change
    carouselProductCondensedStyles?: ProductCarouselProductStyles;
    miniCardStyles?: ProductCarouselProductStyles;
    swiperStyles?: SwiperStyle;
    condensedSwiperStyles?: SwiperStyle;
}

export const productCarouselStyles: ProductCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
};

const defaultCustomCss = `.wrapper {
}

.titleText {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const styles = productCarouselStyles;

const ProductCarousel = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    productRecommendationWidgets,
    sendImpressionTracking,
    session,
    // XNG-Chnages
    isTabletScreen,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);

    useEffect(() => {
        if (fields.carouselType) {
            loadCarouselProducts({
                isShellEdit: shellContext.isInShell && shellContext.isEditing,
                carouselId: id,
                carouselType: fields.carouselType,
                relatedProductType: fields.relatedProductType,
                seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
                displayProductsFrom: fields.displayProductsFrom,
                selectedCategoryIds: fields.selectedCategoryIds,
                numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
                widgetPosition: fields.widgetPosition,
                isProductDetailsPage,
                productId: productContext?.product?.id,
                isProductListPage,
                category,
                isBrandDetailsPage,
                brand,
            });
        }
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        fields.widgetPosition,
        productContext?.product?.id,
        category?.id,
        brand?.id,
        productRecommendationWidgets,
        session?.pickUpWarehouse?.id,
    ]);

    // const [emblaRef, embla] = useEmblaCarousel();
    // const [windowResizeTime, setWindowResizeTime] = useState(0);
    // const [canScrollPrev, setCanScrollPrev] = useState(false);
    // const [canScrollNext, setCanScrollNext] = useState(false);
    // const setCanScroll = () => {
    //     if (shellContext.isInShell) {
    //         return;
    //     }
    //     setCanScrollPrev(!!embla && embla.canScrollPrev());
    //     setCanScrollNext(!!embla && embla.canScrollNext());
    // };

    // const getSlidesToScroll = useCallback(() => {
    //     const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
    //     if (typeof window === "undefined") {
    //         return maxNumberOfColumns;
    //     }

    //     let localSlidesToScroll: number;
    //     const { innerWidth } = window;
    //     if (innerWidth < theme.breakpoints.values[1]) {
    //         localSlidesToScroll = 1;
    //     } else if (innerWidth < theme.breakpoints.values[2]) {
    //         localSlidesToScroll = 2;
    //     } else if (innerWidth < theme.breakpoints.values[3]) {
    //         localSlidesToScroll = 3;
    //     } else {
    //         localSlidesToScroll = 4;
    //     }
    //     return Math.min(localSlidesToScroll, maxNumberOfColumns);
    // }, [fields.maxNumberOfColumns]);

    // const [slidesToScroll, setSlidesToScroll] = useState(0);

    // useEffect(() => {
    //     const newSlidesToScroll = getSlidesToScroll();
    //     setSlidesToScroll(newSlidesToScroll);
    // }, [fields.maxNumberOfColumns, windowResizeTime]);

    // useEffect(() => {
    //     if (!embla) {
    //         return;
    //     }

    //     embla.on("init", setCanScroll);
    //     embla.on("select", setCanScroll);

    //     const onWindowResize = () => {
    //         setWindowResizeTime(Date.now());
    //     };

    //     window.addEventListener("resize", onWindowResize);
    //     onWindowResize();

    //     return () => {
    //         window.removeEventListener("resize", onWindowResize);
    //     };
    // }, [embla]);

    // useEffect(() => {
    //     if (!embla || !products || products.length === 0) {
    //         return;
    //     }

    //     embla.reInit({
    //         align: "start",
    //         slidesToScroll,
    //         watchDrag: isTouchDevice(),
    //         loop: products.length > slidesToScroll,
    //     });
    //     setCanScroll();
    // }, [embla, slidesToScroll, products]);

    useEffect(() => {
        if (
            fields.carouselType !== "recommendedProducts" ||
            !productRecommendationWidgets ||
            fields.widgetPosition > productRecommendationWidgets.length ||
            !products
        ) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        const recommendationIds: number[] = [];
        products?.forEach(o => {
            const id = productRecommendationWidget.recs.find(r => r.refCode === o.productNumber)?.id;
            if (id) {
                recommendationIds.push(id);
            }
        });

        if (recommendationIds.length) {
            sendImpressionTracking({ recommendationIds });
        }
    }, [fields.carouselType, productRecommendationWidgets, products]);

    const getProductRecommendationId = (product: ProductModel) => {
        if (!productRecommendationWidgets || fields.widgetPosition > productRecommendationWidgets.length) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        return productRecommendationWidget.recs.find(r => r.refCode === product.productNumber)?.id;
    };

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    // XNG-Changes
    // const title =
    //     fields.title ||
    //     translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Carousel");
    // const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const swiperProps =
        fields.view === "condensed"
            ? { extendedStyles: styles.condensedSwiperStyles }
            : { extendedStyles: styles.swiperStyles };

    const carouselCardStyles =
        fields.view === "condensed"
            ? styles.carouselProductCondensedStyles
            : fields.view === "miniCard" || (isTabletScreen && fields.view === "grid")
              ? styles.miniCardStyles
              : styles.carouselProductStyles;
    // </XNG-Changes>

    if (!products) {
        const carouselCardSkeletonStyles = {
            productGridContainerStyles: carouselCardStyles?.productGridContainerStyles,
        };
        return (
            <>
                <StyledWrapper>
                    {fields.title && (
                        <Typography
                            {...styles.titleText}
                            {...{ alignment: fields.titleAlignment, italicizeTitle: fields.italicizeTitle }}
                        >
                            {fields.title}
                        </Typography>
                    )}
                    <XngCarousel
                        id={id}
                        navigation={false}
                        spaceBetween={fields.view === "condensed" ? 24 : 22}
                        scrollbar={false}
                        shadowWidth="0px"
                        showArrowsOnMobile={false}
                        {...swiperProps}
                        pagination={false}
                        slidesPerView="auto"
                        draggable={false}
                        className="swiper-with-skeleton"
                    >
                        {Array.from(Array(4).keys()).map(index => (
                            <Fragment key={index}>
                                <ProductCarouselProductSkeleton
                                    showImage={fields.showImage}
                                    showBrand={fields.showBrandName && fields.view !== "condensed"}
                                    showTitle={fields.showTitle}
                                    showPartNumbers={fields.showPartNumbers && fields.view !== "condensed"}
                                    showPrice={fields.showPrice}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList && fields.view !== "condensed"}
                                    showImageBadges={fields.showImageBadges}
                                    showAvailability={fields.showAvailability}
                                    view={isTabletScreen && fields.view === "grid" ? "miniCard" : fields.view}
                                    extendedStyles={carouselCardSkeletonStyles}
                                />
                            </Fragment>
                        ))}
                    </XngCarousel>
                </StyledWrapper>
            </>
        );
    }

    if (products.length === 0) {
        return null;
    }

    return (
        <>
            <StyledWrapper
                data-carousel-type={fields.relatedProductType}
                css={css`
                    ${isProductDetailsPage && hideElementOnPrint}
                    ${customCssWrapper.css}
                `}
            >
                <SkipNav
                    text={translate("Skip Carousel")}
                    extendedStyles={styles.skipCarouselButton}
                    destination={afterCarousel}
                />
                <Typography
                    {...styles.titleText}
                    {...{ alignment: fields.titleAlignment, italicizeTitle: fields.italicizeTitle }}
                >
                    {fields.title}
                </Typography>
                <XngCarousel
                    id={id}
                    navigation={true}
                    spaceBetween={fields.view === "condensed" ? 24 : 22}
                    scrollbar={true}
                    shadowWidth="64px"
                    showArrowsOnMobile={false}
                    {...swiperProps}
                    pagination={false}
                    slidesPerView="auto"
                    draggable={false}
                    className="xng-swiper-with-shadow"
                >
                    {products.map((product, index) => (
                        <Fragment key={product.id}>
                            <ProductCarouselProduct
                                carouselId={id}
                                product={product}
                                recommendationId={getProductRecommendationId(product)}
                                showImage={fields.showImage}
                                showBrand={fields.showBrandName && fields.view !== "condensed"}
                                showTitle={fields.showTitle}
                                showPartNumbers={fields.showPartNumbers && fields.view !== "condensed"}
                                showPrice={fields.showPrice}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList && fields.view !== "condensed"}
                                showImageBadges={fields.showImageBadges}
                                maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                showTextBadges={fields.showTextBadges}
                                maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                extendedStyles={carouselCardStyles}
                                {...{
                                    view: isTabletScreen && fields.view === "grid" ? "miniCard" : fields.view,
                                    showAvailability: fields.showAvailability,
                                }}
                            />
                        </Fragment>
                    ))}
                </XngCarousel>
            </StyledWrapper>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
    { value: "recommendedProducts", displayName: "Recommended Products" },
    // XNG-Changes
    { value: "coveoRecommendedProducts", displayName: "Coveo Recommended Products" },
];

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductCarousel))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "RadioButtonsField",
                defaultValue: "center",
                horizontal: true,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
            },
            // XNG-Change: use italic style for title
            {
                name: fields.italicizeTitle,
                displayName: "Use Italic Font Style For Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 2,
                tab: basicTab,
            },
            // <XNG-Changes>
            {
                name: fields.view,
                displayName: "Card Type",
                editorTemplate: "RadioButtonsField",
                defaultValue: "grid",
                horizontal: true,
                fieldType: "General",
                sortOrder: 3,
                tab: basicTab,
                options: [
                    {
                        displayName: "Grid",
                        value: "grid",
                    },
                    {
                        displayName: "Condensed",
                        value: "miniCard",
                    },
                    {
                        displayName: "Thumbnail",
                        value: "condensed",
                    },
                ],
            },
            // </XNG-Changes>
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "crossSells",
                fieldType: "Contextual",
                sortOrder: 4,
                tab: basicTab,
                // XNG-Changes
                isRequired: true,
                customFilter: (item, pageType, settings) => {
                    const {
                        settingsCollection: {
                            websiteSettings: { enableDynamicRecommendations },
                            productSettings: { enableProductRecommendations },
                        },
                    } = settings;

                    if (!enableDynamicRecommendations && item.value === "topSellers") {
                        return false;
                    }

                    if (!enableProductRecommendations && item.value === "recommendedProducts") {
                        return false;
                    }

                    // if (
                    //     (pageType.type !== "ProductDetailsPage" && item.value === "relatedProducts") ||
                    //     (pageType.type !== "ProductDetailsPage" &&
                    //         pageType.type !== "CartPage" &&
                    //         item.value === "customersAlsoPurchased")
                    // ) {
                    //     return false;
                    // }

                    return true;
                },
            },

            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 5,
                tab: basicTab,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 6,
                tab: basicTab,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                sortOrder: 7,
                tab: basicTab,
            },

            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 8,
                tab: basicTab,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: (item, _advancedFeaturesEnabled, _theme, pageType) =>
                    pageType === "ProductDetailsPage" && item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 9,
                tab: basicTab,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 4,
                defaultValue: 4,
                fieldType: "Contextual",
                sortOrder: 9,
                isVisible: item => item.fields.carouselTemplate === "carousel",
            },
            {
                name: fields.widgetPosition,
                displayName: "Widget Position",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 1,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "recommendedProducts",
                sortOrder: 10,
                tab: basicTab,
            },
            // {
            //     name: fields.visibleElementsTitle,
            //     displayName: "Visible Elements",
            //     editorTemplate: "LabelField",
            //     defaultValue: "",
            //     fieldType: "General",
            //     sortOrder: 11,
            //     tab: basicTab,
            // },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 11,
                tab: basicTab,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                // XNG-Changes
                // isEnabled: () => true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 12,
                tab: basicTab,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 13,
                tab: basicTab,
                isVisible: item => item.fields[fields.view] !== "condensed",
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 14,
                tab: basicTab,
                isVisible: item => item.fields[fields.view] !== "condensed",
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 15,
                tab: basicTab,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 16,
                tab: basicTab,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 17,
                tab: basicTab,
                isVisible: item => item.fields[fields.view] !== "condensed",
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 18,
                tab: basicTab,
            },

            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 19,
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 20,
                tab: basicTab,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 21,
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 22,
                tab: basicTab,
            },
            // {
            //     name: fields.customCSS,
            //     displayName: "Custom CSS",
            //     editorTemplate: "CodeField",
            //     fieldType: "General",
            //     tab: advancedTab,
            //     defaultValue: defaultCustomCss,
            //     isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            //     validate: value => {
            //         if (value === undefined || value === null) {
            //             return "";
            //         }

            //         const result = cssLinter.parse(value, { silent: true });
            //         if (result?.stylesheet?.parsingErrors) {
            //             // the error output at this time only has room for one line so we just show the first error
            //             return result.stylesheet.parsingErrors.length <= 0
            //                 ? ""
            //                 : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
            //         }

            //         return "Unable to parse Css";
            //     },
            //     options: {
            //         mode: "css",
            //         lint: true,
            //         autoRefresh: true,
            //     },
            // },
        ],
    },
};

export default widgetModule;
